// [cursor 2026-06-01] S87-T6 finish-up: compact, honest screen/browser context
// summary for Harper requests. This is the single source of truth for whether
// Harper currently has visible context — used by the Vision UI to show an
// explicit available/degraded/unavailable state. It never fabricates a frame
// description; when no fresh frame exists it reports `available: false`.

export type VisionContextState = "available" | "degraded" | "unavailable";

export interface HarperVisionContext {
  state: VisionContextState;
  /** True only when a real, recent capture exists. */
  available: boolean;
  isElectron: boolean;
  isCapturing: boolean;
  sessionId: string | null;
  frameCount: number;
  capturedAt: string | null;
  note: string;
}

interface VisionStatusInput {
  isElectron: boolean;
  isCapturing: boolean;
  sessionId: string | null;
  frameCount: number;
  privacyMode?: boolean;
  lastFrameAt?: string | null;
}

export function buildHarperVisionContext(
  input: VisionStatusInput,
): HarperVisionContext {
  if (!input.isElectron) {
    return {
      state: "unavailable",
      available: false,
      isElectron: false,
      isCapturing: false,
      sessionId: null,
      frameCount: 0,
      capturedAt: null,
      note: "Vision is desktop-only. Harper has no visible screen context here.",
    };
  }

  if (input.privacyMode) {
    return {
      state: "degraded",
      available: false,
      isElectron: true,
      isCapturing: false,
      sessionId: input.sessionId,
      frameCount: input.frameCount,
      capturedAt: null,
      note: "Privacy mode is on — Harper cannot see your screen.",
    };
  }

  if (!input.isCapturing || input.frameCount === 0) {
    return {
      state: "degraded",
      available: false,
      isElectron: true,
      isCapturing: input.isCapturing,
      sessionId: input.sessionId,
      frameCount: input.frameCount,
      capturedAt: null,
      note: "Vision is idle — no fresh frame attached to Harper yet.",
    };
  }

  return {
    state: "available",
    available: true,
    isElectron: true,
    isCapturing: true,
    sessionId: input.sessionId,
    frameCount: input.frameCount,
    capturedAt: input.lastFrameAt ?? new Date().toISOString(),
    note: "Harper has a recent screen capture attached.",
  };
}
